import { relations } from 'drizzle-orm'
import {
  boolean,
  date,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
} from 'drizzle-orm/pg-core'
import { systemDesignProblem } from './content.js'
import {
  outcomeEnum,
  perceivedDifficultyEnum,
  questionTypeEnum,
  referenceTypeEnum,
} from './enums.js'
import { company, problem } from './problems.js'
import { user } from './user.js'

// =============================================================================
// TABLES
// =============================================================================

/**
 * InterviewExperience - A single person's interview at a company (raw data collection).
 */
export const interviewExperience = pgTable(
  'InterviewExperience',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    companyId: uuid('companyId')
      .notNull()
      .references(() => company.id, { onDelete: 'cascade' }),
    role: text('role'),
    level: text('level'),
    interviewDate: date('interviewDate', { mode: 'date' }),
    outcome: outcomeEnum('outcome'),
    notes: text('notes'),
    contributorInfo: text('contributorInfo'),
    createdById: text('createdById').references(() => user.id, {
      onDelete: 'set null',
    }),
    isVerified: boolean('isVerified').default(false).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [
    index('InterviewExperience_companyId_idx').on(table.companyId),
    index('InterviewExperience_createdById_idx').on(table.createdById),
  ],
)

/**
 * InterviewQuestionReport - A question as reported in a specific interview (raw form, may be partial).
 */
export const interviewQuestionReport = pgTable(
  'InterviewQuestionReport',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    interviewExperienceId: uuid('interviewExperienceId')
      .notNull()
      .references(() => interviewExperience.id, { onDelete: 'cascade' }),
    type: questionTypeEnum('type').notNull(),
    title: text('title').notNull(),
    description: text('description'),
    roundType: text('roundType'),
    perceivedDifficulty: perceivedDifficultyEnum('perceivedDifficulty'),
    notes: text('notes'),
    order: integer('order').default(0).notNull(),
    referenceType: referenceTypeEnum('referenceType')
      .default('novel')
      .notNull(),
    externalUrl: text('externalUrl'),
    baseProblemId: uuid('baseProblemId').references(() => problem.id, {
      onDelete: 'set null',
    }),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [
    index('InterviewQuestionReport_interviewExperienceId_idx').on(
      table.interviewExperienceId,
    ),
  ],
)

/**
 * InterviewQuestionReportAttachment - Screenshots, documents, links for a question report.
 * Type is inferred from the URL (image, Google Doc, video, etc.).
 */
export const interviewQuestionReportAttachment = pgTable(
  'InterviewQuestionReportAttachment',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    interviewQuestionReportId: uuid('interviewQuestionReportId')
      .notNull()
      .references(() => interviewQuestionReport.id, { onDelete: 'cascade' }),
    url: text('url').notNull(),
    caption: text('caption'),
    order: integer('order').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [
    index('InterviewQuestionReportAttachment_reportId_idx').on(
      table.interviewQuestionReportId,
    ),
  ],
)

/**
 * AlgoInterviewExperience - Links coding question reports to Problem entities.
 */
export const algoInterviewExperience = pgTable(
  'AlgoInterviewExperience',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    interviewQuestionReportId: uuid('interviewQuestionReportId')
      .notNull()
      .references(() => interviewQuestionReport.id, { onDelete: 'cascade' }),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.problemId, table.interviewQuestionReportId),
    index('AlgoInterviewExperience_problemId_idx').on(table.problemId),
    index('AlgoInterviewExperience_reportId_idx').on(
      table.interviewQuestionReportId,
    ),
  ],
)

/**
 * SystemDesignInterviewExperience - Links system design question reports to SystemDesignProblem entities.
 */
export const systemDesignInterviewExperience = pgTable(
  'SystemDesignInterviewExperience',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    systemDesignProblemId: uuid('systemDesignProblemId')
      .notNull()
      .references(() => systemDesignProblem.id, { onDelete: 'cascade' }),
    interviewQuestionReportId: uuid('interviewQuestionReportId')
      .notNull()
      .references(() => interviewQuestionReport.id, { onDelete: 'cascade' }),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.systemDesignProblemId, table.interviewQuestionReportId),
    index('SystemDesignInterviewExperience_problemId_idx').on(
      table.systemDesignProblemId,
    ),
    index('SystemDesignInterviewExperience_reportId_idx').on(
      table.interviewQuestionReportId,
    ),
  ],
)

// =============================================================================
// RELATIONS (defined after all tables)
// =============================================================================

export const interviewExperienceRelations = relations(
  interviewExperience,
  ({ one, many }) => ({
    company: one(company, {
      fields: [interviewExperience.companyId],
      references: [company.id],
    }),
    createdBy: one(user, {
      fields: [interviewExperience.createdById],
      references: [user.id],
    }),
    questionReports: many(interviewQuestionReport),
  }),
)

export const interviewQuestionReportRelations = relations(
  interviewQuestionReport,
  ({ one, many }) => ({
    interviewExperience: one(interviewExperience, {
      fields: [interviewQuestionReport.interviewExperienceId],
      references: [interviewExperience.id],
    }),
    attachments: many(interviewQuestionReportAttachment),
    algoLinks: many(algoInterviewExperience),
    systemDesignLinks: many(systemDesignInterviewExperience),
    baseProblem: one(problem, {
      fields: [interviewQuestionReport.baseProblemId],
      references: [problem.id],
    }),
  }),
)

export const interviewQuestionReportAttachmentRelations = relations(
  interviewQuestionReportAttachment,
  ({ one }) => ({
    questionReport: one(interviewQuestionReport, {
      fields: [interviewQuestionReportAttachment.interviewQuestionReportId],
      references: [interviewQuestionReport.id],
    }),
  }),
)

export const algoInterviewExperienceRelations = relations(
  algoInterviewExperience,
  ({ one }) => ({
    problem: one(problem, {
      fields: [algoInterviewExperience.problemId],
      references: [problem.id],
    }),
    questionReport: one(interviewQuestionReport, {
      fields: [algoInterviewExperience.interviewQuestionReportId],
      references: [interviewQuestionReport.id],
    }),
  }),
)

export const systemDesignInterviewExperienceRelations = relations(
  systemDesignInterviewExperience,
  ({ one }) => ({
    systemDesignProblem: one(systemDesignProblem, {
      fields: [systemDesignInterviewExperience.systemDesignProblemId],
      references: [systemDesignProblem.id],
    }),
    questionReport: one(interviewQuestionReport, {
      fields: [systemDesignInterviewExperience.interviewQuestionReportId],
      references: [interviewQuestionReport.id],
    }),
  }),
)

// =============================================================================
// TYPES
// =============================================================================

export type InterviewExperience = typeof interviewExperience.$inferSelect
export type NewInterviewExperience = typeof interviewExperience.$inferInsert
export type InterviewQuestionReport =
  typeof interviewQuestionReport.$inferSelect
export type NewInterviewQuestionReport =
  typeof interviewQuestionReport.$inferInsert
export type InterviewQuestionReportAttachment =
  typeof interviewQuestionReportAttachment.$inferSelect
export type NewInterviewQuestionReportAttachment =
  typeof interviewQuestionReportAttachment.$inferInsert
export type AlgoInterviewExperience =
  typeof algoInterviewExperience.$inferSelect
export type NewAlgoInterviewExperience =
  typeof algoInterviewExperience.$inferInsert
export type SystemDesignInterviewExperience =
  typeof systemDesignInterviewExperience.$inferSelect
export type NewSystemDesignInterviewExperience =
  typeof systemDesignInterviewExperience.$inferInsert
